import { useQuery, QueryClient, UseQueryOptions } from "@tanstack/react-query";
import {
  fetchEsimCollection,
  EsimCollectionModel,
} from "../repositories/esim.repository";

// ---- Query Keys ----
export const esimKeys = {
  all: ["esim"] as const,
  collection: (cusId: string) =>
    [...esimKeys.all, "collection", cusId] as const,
};

// ---- Hook ----
type EsimSelect<T> = (data: EsimCollectionModel) => T;

export function useEsimCollection<TData = EsimCollectionModel>(
  cusId?: string,
  options?: Omit<
    UseQueryOptions<
      EsimCollectionModel,
      Error,
      TData,
      ReturnType<typeof esimKeys.collection>
    >,
    "queryKey" | "queryFn" | "enabled"
  > & {
    /** Extra enabled; defaults to true only when cusId is truthy */
    enabled?: boolean;
    /** Optionally map/shape the result */
    select?: EsimSelect<TData>;
  }
) {
  const enabled = (options?.enabled ?? true) && Boolean(cusId);

  return useQuery({
    queryKey: esimKeys.collection(cusId || "unknown"),
    queryFn: () => fetchEsimCollection(cusId as string),
    enabled,
    staleTime: 5 * 60 * 1000, // 5 mins; tweak as needed
    gcTime: 30 * 60 * 1000, // 30 mins
    ...options,
  });
}

// ---- Helpers ----
export async function prefetchEsimCollection(qc: QueryClient, cusId: string) {
  return qc.prefetchQuery({
    queryKey: esimKeys.collection(cusId),
    queryFn: () => fetchEsimCollection(cusId),
    staleTime: 5 * 60 * 1000,
  });
}

export async function invalidateEsimCollection(qc: QueryClient, cusId: string) {
  return qc.invalidateQueries({ queryKey: esimKeys.collection(cusId) });
}
